import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Button from '../ui/Button';
import { useScrollLock } from '../../hooks/useScrollLock';
import { useContactForm } from '../../contexts/ContactFormContext';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { lockScroll, unlockScroll } = useScrollLock();
  const navigate = useNavigate();
  const location = useLocation();
  const { openForm: openContactForm } = useContactForm();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle menu open/close
  useEffect(() => {
    if (isMenuOpen) {
      lockScroll();
      // Focus trap
      menuRef.current?.focus();
    } else {
      unlockScroll();
    }
  }, [isMenuOpen, lockScroll, unlockScroll]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isMenuOpen]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const navLinks = [
    { href: '/#services', label: 'Services', ariaLabel: 'View our services' },
    { href: '/#portfolio', label: 'Work', ariaLabel: 'View our portfolio' },
    { href: '/#pricing', label: 'Pricing', ariaLabel: 'View pricing plans' },
    { href: '/blog', label: 'Blog', ariaLabel: 'Read our blog' },
    { href: '/#about', label: 'About', ariaLabel: 'Learn about us' },
  ];

  return (
    <>
      <header 
        className={`fixed top-0 z-50 w-full transition-all duration-300 ${
          isScrolled ? 'bg-background/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'
        }`}
        role="banner"
      >
        <div className="container mx-auto px-3 sm:px-4 lg:px-6 flex h-20 items-center justify-between">
          <div className="flex items-center">
            <button 
              onClick={(e) => {
                e.preventDefault();
                console.log('Logo clicked from:', location.pathname);
                if (location.pathname === '/') {
                  // If on home page, smooth scroll to top
                  console.log('Scrolling to top');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  // If on other page, navigate to home page
                  console.log('Navigating to home');
                  navigate('/');
                }
              }}
              className="focus-ring rounded-sm"
              aria-label="Paokhum homepage"
            >
              <img 
                src="/Paokhum_logo_dark.svg" 
                alt="Paokhum" 
                className="h-6 w-auto"
              />
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav 
            className="hidden lg:flex items-center space-x-8 text-sm font-mono uppercase tracking-widest"
            aria-label="Main navigation"
          >
            {navLinks.map(link => {
              // For blog link, use React Router Link
              if (link.href === '/blog') {
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="relative group transition-all duration-300 focus-ring rounded-sm py-2 px-1 text-foreground"
                    aria-label={link.ariaLabel}
                  >
                    <span className="relative z-10 font-medium group-hover:font-extrabold group-hover:tracking-[0.15em] transition-all duration-300">
                      {link.label}
                    </span>
                    <span className="absolute inset-0 z-0 bg-primary/0 group-hover:bg-primary/20 rounded-lg blur-md opacity-0 group-hover:opacity-100 transition-all duration-500 pointer-events-none scale-50 group-hover:scale-150"></span>
                  </Link>
                );
              }
              // For hash links, use custom handler
              return (
                <button
                  key={link.href}
                  onClick={() => {
                    if (link.href.startsWith('/#')) {
                      const hash = link.href.substring(1);
                      if (location.pathname !== '/') {
                        navigate('/' + hash);
                      } else {
                        const element = document.getElementById(hash.substring(1));
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }
                    }
                  }}
                  className="relative group transition-all duration-300 focus-ring rounded-sm cursor-pointer text-left py-2 px-1 text-foreground"
                  aria-label={link.ariaLabel}
                >
                  <span className="relative z-10 font-medium group-hover:font-extrabold group-hover:tracking-[0.15em] transition-all duration-300">
                    {link.label}
                  </span>
                  <span className="absolute inset-0 z-0 bg-primary/0 group-hover:bg-primary/20 rounded-lg blur-md opacity-0 group-hover:opacity-100 transition-all duration-500 pointer-events-none scale-50 group-hover:scale-150"></span>
                </button>
              );
            })}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button 
              variant="primary" 
              onClick={() => openContactForm('Book a Consultation')}
              className="px-6 py-2 pb-2.5 text-sm uppercase tracking-wider font-semibold hover:scale-[1.02] active:scale-[0.98] transition-transform"
            >
              Book a Consultation
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
            className="lg:hidden z-50 relative w-10 h-10 flex flex-col justify-center items-center focus-ring rounded-sm"
          >
            <span 
              className={`block w-6 h-0.5 bg-foreground transform transition duration-300 ease-in-out ${
                isMenuOpen ? 'rotate-45 translate-y-1.5' : ''
              }`}
              aria-hidden="true"
            />
            <span 
              className={`block w-6 h-0.5 bg-foreground transition duration-300 ease-in-out my-1 ${
                isMenuOpen ? 'opacity-0' : ''
              }`}
              aria-hidden="true"
            />
            <span 
              className={`block w-6 h-0.5 bg-foreground transform transition duration-300 ease-in-out ${
                isMenuOpen ? '-rotate-45 -translate-y-1.5' : ''
              }`}
              aria-hidden="true"
            />
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div 
        id="mobile-menu"
        ref={menuRef}
        className={`fixed inset-0 z-40 bg-background transform transition-transform duration-300 ease-in-out lg:hidden ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-hidden={!isMenuOpen}
        tabIndex={-1}
      >
        <div className="container mx-auto px-3 sm:px-4 lg:px-6 h-full flex flex-col justify-center items-center text-center">
          <nav 
            className="flex flex-col items-center space-y-8 text-2xl font-mono uppercase tracking-widest"
            aria-label="Mobile navigation"
          >
            {navLinks.map(link => {
              // For blog link, use React Router Link
              if (link.href === '/blog') {
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={closeMenu}
                    className="transition-colors hover:text-primary focus-ring rounded-sm"
                    aria-label={link.ariaLabel}
                  >
                    {link.label}
                  </Link>
                );
              }
              // For hash links, use button
              return (
                <button
                  key={link.href}
                  onClick={() => {
                    closeMenu();
                    if (link.href.startsWith('/#')) {
                      const hash = link.href.substring(1);
                      if (location.pathname !== '/') {
                        navigate('/' + hash);
                      } else {
                        const element = document.getElementById(hash.substring(1));
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }
                    }
                  }}
                  className="transition-colors hover:text-primary focus-ring rounded-sm"
                  aria-label={link.ariaLabel}
                >
                  {link.label}
                </button>
              );
            })}
          </nav>
          <div className="mt-12">
              <Button 
                variant="primary" 
                onClick={() => {
                  closeMenu();
                  openContactForm('Book a Consultation');
                }}
                className="w-full justify-center px-6 py-3 pb-3.5 mt-4 text-sm uppercase tracking-wider font-semibold"
              >
                Book a Consultation
              </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
